/**
 * Supervisor Performance — management-only side-by-side comparison.
 * Hidden from non-exec users via Sidebar `requires_role` AND defended at
 * the page level (renders an Access Denied panel for non-execs). The backend
 * also returns 403, so this is defence in depth.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, Loader2, Lock, ShieldAlert } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';

const EXEC_ROLES = new Set(['super_admin', 'md', 'ceo', 'cmd', 'cfo']);
const EXEC_DEPTS = new Set(['md', 'ceo', 'cmd', 'cfo', 'executive', 'leadership']);

function isExec(user) {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  const dept = (user.department || '').toLowerCase();
  return EXEC_ROLES.has(role) || EXEC_DEPTS.has(dept);
}

const SORT_KEYS = [
  { k: 'man_hours', label: 'Man Hours' },
  { k: 'present_days', label: 'Present Days' },
  { k: 'attendance_pct', label: 'Attendance %' },
  { k: 'labour_days', label: 'Labour-Days' },
  { k: 'activity_count', label: 'Activities' },
  { k: 'boq_qty_done', label: 'BOQ Qty Done' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgo = (d) => new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);

export default function SupervisorPerformance() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [sortKey, setSortKey] = useState('man_hours');
  const [errMsg, setErrMsg] = useState('');
  const [meta, setMeta] = useState({ date_from: '', date_to: '' });

  const userIsExec = isExec(user);

  const load = async () => {
    setLoading(true);
    setErrMsg('');
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.set('date_from', dateFrom);
      if (dateTo) p.set('date_to', dateTo);
      const d = await apiGet(`/labour-master/supervisor-performance?${p.toString()}`);
      setRows(d.rows || []);
      setMeta({ date_from: d.date_from, date_to: d.date_to });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErrMsg('You do not have permission to view supervisor performance data.');
      } else {
        setErrMsg(err?.response?.data?.detail || 'Failed to load comparison.');
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userIsExec) load();
    // eslint-disable-next-line
  }, [userIsExec, dateFrom, dateTo]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  }, [rows, sortKey]);

  const maxOf = (key) => Math.max(1, ...rows.map((r) => r[key] || 0));

  if (authLoading) {
    return (
      <div className="p-8 text-slate-400 text-sm flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }

  if (!userIsExec) {
    return (
      <div className="p-6" data-testid="supervisor-perf-denied">
        <div className="max-w-xl mx-auto bg-white border border-rose-200 rounded-lg p-8 text-center">
          <Lock size={28} className="mx-auto text-rose-600 mb-2" />
          <div className="font-display text-lg font-bold text-slate-800">Restricted Page</div>
          <div className="text-sm text-slate-600 mt-1">
            Supervisor Performance comparison is available only to MD, CEO, CMD, CFO and Super Admin.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="supervisor-performance-page">
      <PageHeader
        title="Supervisor Performance"
        subtitle="Side-by-side comparison of supervisors based on daily labour assignment + attendance hours."
        icon={<BarChart3 size={18} />}
      />

      {/* Filter strip */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-3" data-testid="sp-filter-strip">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="block mt-1 px-2 py-1 border border-slate-300 rounded text-xs"
            data-testid="sp-date-from"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="block mt-1 px-2 py-1 border border-slate-300 rounded text-xs"
            data-testid="sp-date-to"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sort by</label>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            className="block mt-1 px-2 py-1 border border-slate-300 rounded text-xs bg-white"
            data-testid="sp-sort"
          >
            {SORT_KEYS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
          </select>
        </div>
        <div className="ml-auto text-[10px] text-slate-500">
          Window: <b>{meta.date_from}</b> → <b>{meta.date_to}</b> · <b>{rows.length}</b> supervisor{rows.length === 1 ? '' : 's'}
        </div>
      </div>

      {errMsg && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-800 flex items-center gap-2" data-testid="sp-error">
          <ShieldAlert size={14} /> {errMsg}
        </div>
      )}

      {loading && (
        <div className="text-center text-slate-400 py-10 text-sm flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading comparison…
        </div>
      )}

      {!loading && !rows.length && !errMsg && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400 text-sm">
          No supervisor assignments logged in this window. Once site engineers start
          recording daily supervisor assignments, this comparison will populate.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="sp-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Supervisor</th>
                <th className="text-right px-4 py-2 font-semibold">Labour-Days</th>
                <th className="text-right px-4 py-2 font-semibold">Unique Labour</th>
                <th className="text-right px-4 py-2 font-semibold">Present Days</th>
                <th className="text-right px-4 py-2 font-semibold">Attendance %</th>
                <th className="text-right px-4 py-2 font-semibold">Man Hours</th>
                <th className="text-right px-4 py-2 font-semibold">Avg hrs / present-day</th>
                <th className="text-right px-4 py-2 font-semibold">Activities</th>
                <th className="text-right px-4 py-2 font-semibold">BOQ-linked</th>
                <th className="text-right px-4 py-2 font-semibold">BOQ Qty Done</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => {
                const mhPct = Math.round((r.man_hours / maxOf('man_hours')) * 100);
                return (
                  <tr key={r.supervisor_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sp-row-${r.supervisor_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-display font-bold text-slate-800">{r.supervisor_name || '—'}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{r.supervisor_id}</div>
                      <div className="mt-1 h-1.5 w-32 bg-slate-100 rounded overflow-hidden">
                        <div className="h-full bg-[#1F7A6E]" style={{ width: `${mhPct}%` }} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">{r.labour_days}</td>
                    <td className="px-4 py-3 text-right">{r.unique_labour}</td>
                    <td className="px-4 py-3 text-right">{r.present_days}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        r.attendance_pct >= 80 ? 'bg-emerald-100 text-emerald-700' :
                        r.attendance_pct >= 50 ? 'bg-amber-100 text-amber-700' :
                        'bg-rose-100 text-rose-700'
                      }`}>
                        {r.attendance_pct}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-display font-bold">{r.man_hours}</td>
                    <td className="px-4 py-3 text-right">{r.avg_hours_per_present_day}</td>
                    <td className="px-4 py-3 text-right">{r.activity_count}</td>
                    <td className="px-4 py-3 text-right text-slate-500">{r.boq_linked_activities}</td>
                    <td className="px-4 py-3 text-right font-medium">{r.boq_qty_done}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
